import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Check, X, Upload, Eye } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { fmtDate, istToday } from "@/lib/dateFormat";

const DS = { "Not Requested": "bg-zinc-200 text-zinc-600", "Pending Approval": "bg-amber-100 text-amber-700", "Approved": "bg-blue-100 text-blue-700", "Rejected": "bg-red-100 text-red-700", "Disposed": "bg-green-100 text-green-700" };

export default function DamagedStockPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdmin = perms.includes("admin.full_access");
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const DISPOSAL_METHODS = getValues("disposal_methods");

  const [damaged, setDamaged] = useState([]);
  const [repackItems, setRepackItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Request Disposal dialog
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestItem, setRequestItem] = useState(null);
  const [disposalMethod, setDisposalMethod] = useState("");
  const [disposalNotes, setDisposalNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Approve/Reject dialog
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approveItem, setApproveItem] = useState(null);
  const [approveAction, setApproveAction] = useState("approve");
  const [approveComments, setApproveComments] = useState("");

  // Confirm Disposal dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmDate, setConfirmDate] = useState(istToday());
  const [confirmBatch, setConfirmBatch] = useState("");
  const [confirmPhoto, setConfirmPhoto] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  // Repack dialog
  const [showRepackDialog, setShowRepackDialog] = useState(false);
  const [repackItem, setRepackItem] = useState(null);
  const [repackQty, setRepackQty] = useState(0);
  const [repackBatch, setRepackBatch] = useState("");
  const [repackDate, setRepackDate] = useState(istToday());

  const load = async () => {
    setLoading(true);
    try {
      const [damRes, repRes] = await Promise.all([api.get("/damaged-stock"), api.get("/repack-stock")]);
      setDamaged(damRes.data);
      setRepackItems(repRes.data);
    } catch (err) { console.error("API get /damaged-stock error:", err); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pendingApproval = damaged.filter(d => d.disposal_status === "Pending Approval");

  const requestDisposal = async () => {
    if (!disposalMethod) { toast.error("Disposal method is required"); return; }
    if (!disposalNotes.trim()) { toast.error("Notes are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/damaged-stock/request-disposal", { rto_id: requestItem.rto_id, sku_id: requestItem.sku_id, sku_name: requestItem.sku_name, damaged_qty: requestItem.damaged_qty, disposal_method: disposalMethod, notes: disposalNotes });
      toast.success("Disposal request sent to Admin for approval");
      setShowRequestDialog(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const handleApprove = async () => {
    if (approveAction === "reject" && !approveComments.trim()) { toast.error("Rejection reason is mandatory"); return; }
    try {
      await api.put(`/damaged-stock/${approveItem.disposal_id}/approve`, { action: approveAction, comments: approveComments });
      toast.success(approveAction === "approve" ? "Disposal approved" : "Disposal rejected");
      setShowApproveDialog(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const confirmDisposal = async () => {
    if (!confirmChecked) { toast.error("Please confirm the disposal checkbox"); return; }
    try {
      // Upload photo if provided
      if (confirmPhoto) {
        const formData = new FormData();
        formData.append("file", confirmPhoto);
        formData.append("entity_type", "disposal");
        formData.append("entity_id", confirmItem.disposal_id);
        formData.append("document_type", "disposal_photo");
        await api.post("/documents/upload", formData);
      }
      await api.put(`/damaged-stock/${confirmItem.disposal_id}/approve`, { action: "dispose", comments: `Disposed on ${confirmDate}. Batch: ${confirmBatch}` });
      toast.success("Disposal confirmed");
      setShowConfirmDialog(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="damaged-stock-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Damaged Stock</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage damaged RTO stock and disposal workflow</p>
      </div>

      {/* Admin: Pending Approval Banner */}
      {isAdmin && pendingApproval.length > 0 && (
        <div className="mb-4 border border-amber-300 bg-amber-50 p-3 rounded-sm" data-testid="disposal-approval-banner">
          <p className="text-xs font-semibold text-amber-800 mb-2">{pendingApproval.length} disposal request(s) pending your approval</p>
          {pendingApproval.map((d, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-amber-200 last:border-0">
              <div className="text-xs"><span className="font-medium">{d.sku_name}</span> — {d.damaged_qty} boxes — from {d.rto_number}</div>
              <div className="flex gap-1">
                <Button className="rounded-sm h-6 text-[10px] px-2" onClick={() => { setApproveItem(d); setApproveAction("approve"); setApproveComments(""); setShowApproveDialog(true); }}><Check size={10} /> Approve</Button>
                <Button variant="outline" className="rounded-sm h-6 text-[10px] px-2 border-red-300 text-red-600" onClick={() => { setApproveItem(d); setApproveAction("reject"); setApproveComments(""); setShowApproveDialog(true); }}><X size={10} /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="damaged" className="space-y-3">
        <TabsList className="bg-zinc-100 rounded-sm h-8">
          <TabsTrigger value="damaged" className="text-xs rounded-sm h-6">Damaged Stock</TabsTrigger>
          <TabsTrigger value="repack" className="text-xs rounded-sm h-6">Repack Queue</TabsTrigger>
        </TabsList>

        <TabsContent value="damaged">
          <div className="border border-zinc-200 overflow-x-auto">
            <table className="data-table w-full">
              <thead><tr><th>SKU</th><th className="num">Damaged Qty</th><th>Damage Type</th><th>Source RTO</th><th className="num">Repackable</th><th>Received</th><th>Disposal Status</th><th className="w-24">Actions</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
                damaged.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">No damaged stock records</td></tr> :
                damaged.map((d, i) => (
                  <tr key={i} data-testid={`damaged-row-${i}`}>
                    <td className="text-xs font-medium">{d.sku_name}</td>
                    <td className="num font-mono font-semibold text-red-600">{d.damaged_qty}</td>
                    <td className="text-xs">{d.damage_type}</td>
                    <td className="text-xs"><button onClick={() => navigate(`/returns/${d.rto_id}`)} className="text-blue-600 hover:underline font-mono">{d.rto_number}</button></td>
                    <td className="num font-mono">{d.repackable_units || "—"}</td>
                    <td className="text-xs text-zinc-500">{fmtDate(d.received_at)}</td>
                    <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${DS[d.disposal_status] || DS["Not Requested"]}`}>{d.disposal_status}</span></td>
                    <td>
                      <div className="flex gap-1">
                        {d.disposal_status === "Not Requested" && (
                          <Button variant="outline" className="rounded-sm h-6 text-[10px] px-2 border-red-300 text-red-600" onClick={() => { setRequestItem(d); setDisposalMethod(""); setDisposalNotes(""); setShowRequestDialog(true); }} data-testid={`request-disposal-${i}`}><Trash2 size={10} /> Dispose</Button>
                        )}
                        {d.disposal_status === "Approved" && (
                          <Button className="rounded-sm h-6 text-[10px] px-2" onClick={() => { setConfirmItem(d); setConfirmChecked(false); setConfirmPhoto(null); setShowConfirmDialog(true); }} data-testid={`confirm-disposal-${i}`}><Check size={10} /> Confirm</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="repack">
          <div className="border border-zinc-200 overflow-x-auto">
            <table className="data-table w-full">
              <thead><tr><th>SKU</th><th className="num">Units</th><th>MFD</th><th>Expiry</th><th>Source RTO</th><th>Status</th><th className="w-40">Actions</th></tr></thead>
              <tbody>
                {repackItems.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-zinc-400 text-xs">No items in repack queue</td></tr> :
                repackItems.map((item, i) => {
                  const RSC = { "Pending Repack": "bg-amber-100 text-amber-700", "Sent to Production": "bg-blue-100 text-blue-700", "Repackaged": "bg-green-100 text-green-700", "Disposed": "bg-zinc-200 text-zinc-600" };
                  return (
                    <tr key={item._id} data-testid={`repack-row-${i}`}>
                      <td className="text-xs font-medium">{item.sku_name}</td>
                      <td className="num font-mono font-semibold">{item.units_count}</td>
                      <td className="text-xs">{item.production_date ? fmtDate(item.production_date) : "—"}</td>
                      <td className="text-xs">{item.expiry_date ? fmtDate(item.expiry_date) : "—"}</td>
                      <td className="text-xs font-mono">{item.source_rto_number || "—"}</td>
                      <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${RSC[item.status] || ''}`}>{item.status}</span></td>
                      <td>
                        <div className="flex gap-1">
                          {item.status === "Pending Repack" && (
                            <Button variant="outline" className="rounded-sm h-6 text-[10px] px-2" onClick={async () => {
                              try { await api.put(`/repack-stock/${item._id}/status`, { status: "Sent to Production" }); toast.success("Sent to production"); load(); }
                              catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                            }}>Send to Production</Button>
                          )}
                          {item.status === "Sent to Production" && isAdmin && (
                            <Button className="rounded-sm h-6 text-[10px] px-2" onClick={() => { setRepackItem(item); setRepackQty(item.units_count); setRepackBatch(""); setShowRepackDialog(true); }}>Confirm Repackaged</Button>
                          )}
                          {(item.status === "Pending Repack" || item.status === "Sent to Production") && (
                            <Button variant="outline" className="rounded-sm h-6 text-[10px] px-2 border-red-300 text-red-600" onClick={async () => {
                              const reason = window.prompt("Disposal reason (mandatory):");
                              if (!reason) return;
                              try { await api.put(`/repack-stock/${item._id}/status`, { status: "Disposed", reason }); toast.success("Marked as disposed"); load(); }
                              catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                            }}>Dispose</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Request Disposal Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-sm" data-testid="request-disposal-dialog">
          <DialogHeader><DialogTitle className="font-heading text-sm">Request Disposal</DialogTitle></DialogHeader>
          {requestItem && <p className="text-xs text-zinc-500">{requestItem.sku_name} — {requestItem.damaged_qty} boxes from {requestItem.rto_number}</p>}
          <div className="space-y-3">
            <div><Label className="text-xs">Disposal Method *</Label>
              <Select value={disposalMethod || "none"} onValueChange={v => setDisposalMethod(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1 rounded-sm text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- Select --</SelectItem>{DISPOSAL_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Disposal Notes *</Label><Textarea value={disposalNotes} onChange={e => setDisposalNotes(e.target.value)} className="mt-1 rounded-sm text-xs" rows={3} placeholder="Describe the disposal plan..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={requestDisposal} disabled={submitting} className="rounded-sm text-xs">{submitting ? "Sending..." : "Request Disposal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-sm">{approveAction === "approve" ? "Approve Disposal" : "Reject Disposal"}</DialogTitle></DialogHeader>
          {approveItem && <p className="text-xs text-zinc-500">{approveItem.sku_name} — {approveItem.damaged_qty} boxes</p>}
          <div><Label className="text-xs">{approveAction === "reject" ? "Rejection Reason *" : "Comments (optional)"}</Label><Textarea value={approveComments} onChange={e => setApproveComments(e.target.value)} className="mt-1 rounded-sm text-xs" rows={2} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleApprove} className={`rounded-sm text-xs ${approveAction === "reject" ? "bg-red-600 hover:bg-red-700" : ""}`}>{approveAction === "approve" ? "Approve" : "Reject"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Disposal Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-sm" data-testid="confirm-disposal-dialog">
          <DialogHeader><DialogTitle className="font-heading text-sm">Confirm Disposal</DialogTitle></DialogHeader>
          {confirmItem && <p className="text-xs text-zinc-500">{confirmItem.sku_name} — {confirmItem.damaged_qty} boxes</p>}
          <div className="space-y-3">
            <div><Label className="text-xs">Disposal Date</Label><Input type="date" value={confirmDate} onChange={e => setConfirmDate(e.target.value)} className="mt-1 rounded-sm text-xs" /></div>
            <div><Label className="text-xs">Batch Numbers Disposed</Label><Input value={confirmBatch} onChange={e => setConfirmBatch(e.target.value)} className="mt-1 rounded-sm text-xs" /></div>
            <div><Label className="text-xs">Upload Disposal Photo (mandatory)</Label><Input type="file" accept="image/*" onChange={e => setConfirmPhoto(e.target.files?.[0] || null)} className="mt-1 rounded-sm text-xs" /></div>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={confirmChecked} onChange={e => setConfirmChecked(e.target.checked)} className="rounded" /><span className="text-xs">I confirm this stock has been disposed as approved</span></label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={confirmDisposal} disabled={!confirmChecked} className="rounded-sm text-xs">Confirm Disposal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Repackaged Dialog */}
      <Dialog open={showRepackDialog} onOpenChange={setShowRepackDialog}>
        <DialogContent className="max-w-sm" data-testid="repack-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading text-sm">Confirm Repackaging Complete</DialogTitle></DialogHeader>
          {repackItem && <p className="text-xs text-zinc-500">{repackItem.sku_name} — {repackItem.units_count} units sent to production</p>}
          <div className="space-y-3">
            <div><Label className="text-xs">Repackaged Qty (units)</Label><Input type="number" min={0} max={repackItem?.units_count || 999} value={repackQty} onChange={e => setRepackQty(parseInt(e.target.value) || 0)} className="mt-1 rounded-sm text-xs" /></div>
            <div><Label className="text-xs">New Batch Number</Label><Input value={repackBatch} onChange={e => setRepackBatch(e.target.value)} className="mt-1 rounded-sm text-xs" placeholder="e.g. REPACK-001" /></div>
            <div><Label className="text-xs">Repackaged Date</Label><Input type="date" value={repackDate} onChange={e => setRepackDate(e.target.value)} className="mt-1 rounded-sm text-xs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepackDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={async () => {
              try {
                await api.put(`/repack-stock/${repackItem._id}/status`, { status: "Repackaged", repackaged_qty: repackQty, new_batch_number: repackBatch });
                toast.success(`${repackQty} units repackaged and added to inventory`);
                setShowRepackDialog(false); load();
              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }} className="rounded-sm text-xs" data-testid="confirm-repack-btn">Confirm Repackaged</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
